/**
 * Hook for per-user viewed/unread report tracking
 *
 * Backed by the ReportViews join table (user + reportId). A report is "viewed"
 * once the user has opened its details screen; everything else is "unread".
 * Powers the "Unread only" filter on the report list screens.
 */
import { useState, useEffect, useCallback } from 'react';

import Util from '../util/util';

/**
 * Persist that the current user viewed a report. Fire-and-forget: a failed
 * write must never break the details screen, so errors are swallowed.
 * @param {string} reportId - Report uuid
 */
export async function markReportViewed(reportId) {
  if (!reportId) return;
  try {
    await Util.post('Resty', '/items/report-views', { body: { reportId } });
  } catch (err) {
    console.log('Failed to mark report viewed:', err?.message || err);
  }
}

/**
 * Main hook: loads the caller's viewed report ids and exposes helpers for
 * unread checks and optimistic local updates.
 */
export default function useViewedReports() {
  // null until loaded — isViewed() treats unknown as viewed so the UI
  // doesn't flash every report as unread while the set is loading.
  const [viewedIds, setViewedIds] = useState(null);
  const [viewedLoading, setViewedLoading] = useState(false);

  const refreshViewed = useCallback(async () => {
    setViewedLoading(true);
    try {
      const data = await Util.get('Resty', '/items/report-views', {});
      const ids = (data.items || []).map((item) => item.reportId).filter(Boolean);
      setViewedIds(new Set(ids));
    } catch (err) {
      console.log('Failed to load viewed reports:', err?.message || err);
    } finally {
      setViewedLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshViewed();
  }, [refreshViewed]);

  /**
   * True when the report is known-viewed OR the set hasn't loaded yet.
   */
  const isViewed = useCallback(
    (reportId) => viewedIds === null || viewedIds.has(reportId),
    [viewedIds]
  );

  /**
   * Optimistically add a report to the local viewed set (no API call — the
   * details screen persists the view via markReportViewed on open).
   */
  const markViewedLocal = useCallback((reportId) => {
    if (!reportId) return;
    setViewedIds((prev) => {
      if (prev === null || prev.has(reportId)) return prev;
      const next = new Set(prev);
      next.add(reportId);
      return next;
    });
  }, []);

  return {
    viewedIds,
    viewedLoading,
    isViewed,
    markViewedLocal,
    refreshViewed,
  };
}
